import type { Ecriture, Balance, SoldeCompte } from '@shared/types/Ecriture';
import { PLAN_COMPTABLE_OHADA } from '@shared/constants/PlanComptable';

/**
 * Service de calcul de la balance generale.
 *
 * Agrege les ecritures par compte et calcule debit/credit/solde pour chacun.
 * Pure : aucun acces a la base, recoit la liste des ecritures en parametre.
 */
export class BalanceService {
  /**
   * Calcule la balance pour une liste d ecritures.
   * @param ecritures Ecritures de la periode ou cumul
   * @returns Map code_compte -> SoldeCompte
   */
  static calculer(ecritures: readonly Ecriture[]): Balance {
    const balance: Balance = {};
    for (const ecr of ecritures) {
      if (!balance[ecr.compte]) {
        balance[ecr.compte] = {
          compte: ecr.compte,
          libelle_compte: ecr.libelle_compte ?? PLAN_COMPTABLE_OHADA[ecr.compte]?.libelle ?? '',
          debit: 0,
          credit: 0,
          solde: 0,
        };
      }
      balance[ecr.compte]!.debit += ecr.debit;
      balance[ecr.compte]!.credit += ecr.credit;
    }
    for (const compte of Object.keys(balance)) {
      const ligne = balance[compte]!;
      ligne.solde = ligne.debit - ligne.credit;
    }
    return balance;
  }

  /**
   * Verifie que la balance est equilibree (Σ debit = Σ credit).
   */
  static estEquilibree(balance: Balance, tolerance = 1): boolean {
    let totalDebit = 0,
      totalCredit = 0;
    for (const ligne of Object.values(balance)) {
      totalDebit += ligne.debit;
      totalCredit += ligne.credit;
    }
    return Math.abs(totalDebit - totalCredit) <= tolerance;
  }

  /**
   * Filtre la balance par classe comptable (1 a 8).
   */
  static parClasse(balance: Balance, classe: number): SoldeCompte[] {
    return Object.values(balance).filter((b) => b.compte.startsWith(String(classe)));
  }
}
